// src/scene/StageGround.tsx
/**
 * Stage Ground Component
 *
 * Provides grounding for 3D objects with:
 * - AccumulativeShadows OR ContactShadows
 * - Subtle ground plane with gradient fade
 * - Optional fog for depth
 *
 * Ensures objects don't "float" in space.
 */

import { useRef, useMemo } from 'react'
import {
  ContactShadows,
  AccumulativeShadows,
  RandomizedLight,
  Plane,
  Circle,
} from '@react-three/drei'
import { Mesh, Color, ShaderMaterial } from 'three'
import { palette, world } from './tokens'

// ============================================================================
// GROUND COMPONENT
// ============================================================================

interface StageGroundProps {
  /** Ground Y position */
  y?: number
  /** Shadow type */
  shadowType?: 'contact' | 'accumulative' | 'none'
  /** Show visible ground plane */
  showPlane?: boolean
  /** Ground plane style */
  planeStyle?: 'circle' | 'infinite' | 'gradient'
  /** Shadow opacity */
  shadowOpacity?: number
  /** Shadow blur */
  shadowBlur?: number
}

export function StageGround({
  y = world.ground.y,
  shadowType = 'contact',
  showPlane = true,
  planeStyle = 'gradient',
  shadowOpacity = 0.5,
  shadowBlur = 2.5,
}: StageGroundProps) {
  return (
    <group>
      {/* Shadow layer */}
      {shadowType === 'contact' && (
        <ContactShadows
          position={[0, y + 0.001, 0]}
          opacity={shadowOpacity}
          scale={20}
          blur={shadowBlur}
          far={6}
          resolution={512}
          color={palette.background.deep}
          frames={1}
        />
      )}

      {shadowType === 'accumulative' && (
        <AccumulativeShadows
          position={[0, y + 0.001, 0]}
          temporal
          frames={60}
          alphaTest={0.85}
          opacity={shadowOpacity}
          scale={20}
          color={palette.background.deep}
        >
          <RandomizedLight
            amount={4}
            radius={6}
            ambient={0.5}
            intensity={1}
            position={[5, 6, 4]}
            bias={0.001}
          />
        </AccumulativeShadows>
      )}

      {/* Ground plane */}
      {showPlane && planeStyle === 'circle' && (
        <Circle
          args={[world.ground.radius, 64]}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, y, 0]}
          receiveShadow
        >
          <meshStandardMaterial
            color={palette.background.base}
            roughness={0.9}
            metalness={0.02}
            envMapIntensity={0.2}
          />
        </Circle>
      )}

      {showPlane && planeStyle === 'gradient' && (
        <GradientGround y={y} />
      )}

      {showPlane && planeStyle === 'infinite' && (
        <Plane
          args={[100, 100]}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, y, 0]}
          receiveShadow
        >
          <meshStandardMaterial
            color={palette.background.base}
            roughness={0.95}
            metalness={0.0}
            envMapIntensity={0.1}
          />
        </Plane>
      )}
    </group>
  )
}

// ============================================================================
// GRADIENT GROUND (fades at edges)
// ============================================================================

function GradientGround({ y }: { y: number }) {
  const meshRef = useRef<Mesh>(null)

  const shaderMaterial = useMemo(() => {
    return new ShaderMaterial({
      uniforms: {
        uColorCenter: { value: new Color(palette.background.base) },
        uColorEdge: { value: new Color(palette.background.deep) },
        uRadius: { value: world.ground.radius },
        uFadeStart: { value: 0.5 },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vPosition;

        void main() {
          vUv = uv;
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColorCenter;
        uniform vec3 uColorEdge;
        uniform float uRadius;
        uniform float uFadeStart;

        varying vec2 vUv;
        varying vec3 vPosition;

        void main() {
          float dist = length(vPosition.xz) / uRadius;
          float fade = smoothstep(uFadeStart, 1.0, dist);

          vec3 color = mix(uColorCenter, uColorEdge, fade);
          float alpha = 1.0 - smoothstep(0.8, 1.0, dist);

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
    })
  }, [])

  return (
    <mesh
      ref={meshRef}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, y, 0]}
      receiveShadow
    >
      <circleGeometry args={[world.ground.radius, 64]} />
      <primitive object={shaderMaterial} attach="material" />
    </mesh>
  )
}

// ============================================================================
// ENVIRONMENT SPHERE (background)
// ============================================================================

export function EnvironmentSphere() {
  return (
    <mesh scale={[-1, 1, 1]}>
      <sphereGeometry args={[world.environment.radius, 32, 32]} />
      <meshBasicMaterial color={palette.background.deep} side={2} />
    </mesh>
  )
}

// ============================================================================
// SCENE FOG
// ============================================================================

interface SceneFogProps {
  /** Fog color */
  color?: string
  /** Near distance */
  near?: number
  /** Far distance */
  far?: number
}

export function SceneFog({
  color = palette.background.deep,
  near = 8,
  far = 30,
}: SceneFogProps) {
  return <fog attach="fog" args={[color, near, far]} />
}

// ============================================================================
// COMPLETE STAGE ENVIRONMENT
// ============================================================================

interface StageEnvironmentProps {
  groundY?: number
  shadowType?: 'contact' | 'accumulative' | 'none'
  showFog?: boolean
}

export function StageEnvironment({
  groundY = world.ground.y,
  shadowType = 'contact',
  showFog = true,
}: StageEnvironmentProps) {
  return (
    <>
      {/* Background sphere */}
      <EnvironmentSphere />

      {/* Ground with shadows */}
      <StageGround
        y={groundY}
        shadowType={shadowType}
        showPlane={true}
        planeStyle="gradient"
      />

      {/* Atmospheric fog */}
      {showFog && <SceneFog near={10} far={35} />}
    </>
  )
}

export default StageGround
